import { useCallback } from 'react';
import { flushSync } from 'react-dom';

import { applyThemeToDocument } from './applyThemeToDocument';
import { type Theme, useThemeStore } from './useThemeStore';

/**
 * Start and end shapes of the sweep, per origin corner. The 200% reach is
 * deliberate: it guarantees the triangle covers the whole viewport at any
 * aspect ratio, so the new theme never lands with a corner still uncovered.
 */
const CLIP_PATHS = {
  'top-start': ['polygon(0% 0%, 0% 0%, 0% 0%)', 'polygon(0% 0%, 200% 0%, 0% 200%)'],
  'top-end': ['polygon(100% 0%, 100% 0%, 100% 0%)', 'polygon(100% 0%, -100% 0%, 100% 200%)'],
} as const;

const DURATION_MS = 700;
const EASING = 'cubic-bezier(.65, 0, .35, 1)';

interface ThemeSwitch {
  readonly theme: Theme;
  readonly toggle: () => void;
}

/**
 * The theme toggle, wrapped in a diagonal reveal.
 *
 * A theme flip is the one change that repaints every pixel at once, and an
 * instant swap reads as a glitch. A straight 45° edge sweeping from the corner
 * the control sits in makes it read as one deliberate movement instead.
 *
 * Everything here degrades: without the View Transitions API, with reduced
 * motion, or mid-transition, the theme still flips — just without the sweep.
 */
export function useThemeSwitch(): ThemeSwitch {
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);

  const toggle = useCallback((): void => {
    const next: Theme = useThemeStore.getState().theme === 'dark' ? 'light' : 'dark';

    const apply = (): void => {
      // flushSync so the DOM carries the new theme before the browser takes its
      // "after" snapshot — a normal setState would land a frame too late
      flushSync(() => {
        setTheme(next);
        applyThemeToDocument(next);
      });
    };

    if (!supportsViewTransitions() || prefersReducedMotion()) {
      apply();
      return;
    }

    const transition = document.startViewTransition(apply);

    void transition.ready
      .then(() => {
        // the RTL header keeps its controls on the start side, the LTR one on
        // the end side; the sweep starts from wherever the button actually is
        const corner = document.documentElement.dir === 'rtl' ? 'top-start' : 'top-end';
        const [from, to] = CLIP_PATHS[corner];

        document.documentElement.animate(
          { clipPath: [from, to] },
          {
            duration: DURATION_MS,
            easing: EASING,
            pseudoElement: '::view-transition-new(root)',
          },
        );
      })
      .catch(() => {
        // a skipped transition is not an error — the theme already changed
      });
  }, [setTheme]);

  return { theme, toggle };
}

function supportsViewTransitions(): boolean {
  return typeof document.startViewTransition === 'function';
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
